/**
 * ENG-5641 — rolling connectivity-probe orchestration (manager CLI).
 *
 * Given an agent's installed integrations, selects the ones DUE for a probe
 * (last_connectivity_check_at older than the interval, NULLS first), bounds the
 * batch so we don't probe everything at once (the rolling cadence: wake every
 * 5 min, probe each integration ~hourly), runs each probe via the executor, and
 * returns the report batch to POST to /host/integration-connectivity.
 *
 * Pure + injectable (executor, clock, interval, batch cap) so due-selection +
 * batching + report-mapping are unit-testable. The real probe capabilities
 * (fetch / runCli / mcpProbe) and the POST live in the poll-loop wiring.
 */

import {
  executeConnectivityProbe as defaultExecuteProbe,
  type ConnectivityProbeDeps,
  type ConnectivityProbeTarget,
} from './connectivity-probe-executor.js';
import type {
  ConnectivityProbeOutcome,
  ConnectivityTestOverride,
  ToolkitSourceType,
} from '@augmented/core/integrations';
import type { IntegrationAuthType } from '@augmented/core';

/** One installed integration as returned (enriched) by /host/agent-integrations. */
export interface ProbeRunnerIntegration {
  id: string;
  definition_id: string;
  scope: 'agent' | 'team' | 'organization';
  auth_type?: IntegrationAuthType | null;
  source_type?: ToolkitSourceType | null;
  credentials?: Record<string, unknown>;
  last_connectivity_check_at?: string | null;
  /** CLI binary name (when this is a cli_tool integration). */
  cli_binary?: string;
  /** MCP server key in the agent's .mcp.json (when this is an mcp_server integration). */
  mcp_server_key?: string;
  /**
   * ENG-6242 — the toolkit's `connectivity_test` override (managed/Composio),
   * enriched by `/host/agent-integrations`. Threaded to the executor so the
   * managed tool-call leg calls the prescribed tool, matching the Test path.
   */
  connectivity_test?: ConnectivityTestOverride | null;
}

export interface ConnectivityReport {
  integration_id: string;
  scope: 'agent' | 'team' | 'organization';
  status: ConnectivityProbeOutcome['status'];
  message?: string;
}

export interface ProbeRunnerOptions {
  now?: () => Date;
  /** A row is due when its last check is older than this (default 1h). */
  intervalMs?: number;
  /** Max probes per run — bounds the rolling batch (default 25). */
  maxPerRun?: number;
  /** Probe capabilities forwarded to the executor (fetch / runCli / mcpProbe / composioProbe). */
  probeDeps?: ConnectivityProbeDeps;
  /** Override the executor (tests). */
  executeProbe?: typeof defaultExecuteProbe;
}

export interface ProbeRunnerResult {
  /** Reports to POST up (only integrations that produced an outcome). */
  reports: ConnectivityReport[];
  /** How many integrations were due this run. */
  due: number;
  /** How many were actually probed (bounded by maxPerRun). */
  probed: number;
  /** Probed but produced no outcome (unsupported / un-wired capability). */
  skipped: number;
}

const DEFAULT_INTERVAL_MS = 60 * 60 * 1000; // 1 hour
const DEFAULT_MAX_PER_RUN = 25;

function isDue(lastCheck: string | null | undefined, now: number, intervalMs: number): boolean {
  if (!lastCheck) return true; // never probed
  const t = Date.parse(lastCheck);
  if (Number.isNaN(t)) return true; // unparseable → treat as due
  return now - t >= intervalMs;
}

export async function runConnectivityProbes(
  integrations: ProbeRunnerIntegration[],
  options: ProbeRunnerOptions = {},
): Promise<ProbeRunnerResult> {
  const now = (options.now?.() ?? new Date()).getTime();
  const intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS;
  const maxPerRun = options.maxPerRun ?? DEFAULT_MAX_PER_RUN;
  const probeDeps = options.probeDeps ?? {};
  const execute = options.executeProbe ?? defaultExecuteProbe;

  // Due rows, oldest-first (NULLS first) so the rolling cursor clears the
  // backlog fairly and load spreads across ticks.
  const due = integrations
    .filter((i) => isDue(i.last_connectivity_check_at, now, intervalMs))
    .sort((a, b) => {
      const ta = a.last_connectivity_check_at ? Date.parse(a.last_connectivity_check_at) : 0;
      const tb = b.last_connectivity_check_at ? Date.parse(b.last_connectivity_check_at) : 0;
      return ta - tb;
    });

  const batch = due.slice(0, maxPerRun);
  const reports: ConnectivityReport[] = [];
  let skipped = 0;

  for (const integ of batch) {
    const target: ConnectivityProbeTarget = {
      definitionId: integ.definition_id,
      sourceType: integ.source_type ?? null,
      authType: integ.auth_type ?? null,
      credentials: integ.credentials ?? {},
      cliBinary: integ.cli_binary,
      mcpServerKey: integ.mcp_server_key,
      connectivityTest: integ.connectivity_test ?? null,
    };
    let outcome: ConnectivityProbeOutcome | null;
    try {
      outcome = await execute(target, probeDeps);
    } catch (err) {
      // A probe blowing up must not fail the batch — record nothing for it.
      console.warn(`[connectivity-probe] ${integ.definition_id} threw: ${(err as Error).message}`);
      outcome = null;
    }
    if (!outcome) {
      skipped += 1;
      continue;
    }
    reports.push({
      integration_id: integ.id,
      scope: integ.scope,
      status: outcome.status,
      ...(outcome.message ? { message: outcome.message } : {}),
    });
  }

  return { reports, due: due.length, probed: batch.length, skipped };
}
